// src/pages/BlogDetail.jsx
import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/forum/Navbar';
import { useBadge } from '../contexts/BadgeContext';

const formatDistanceToNow = (date) => {
  if (!date) return '';
  const now = new Date();
  const diff = Math.abs(now - new Date(date));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days < 1) {
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) {
      const minutes = Math.floor(diff / (1000 * 60));
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    }
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else if (days < 30) {
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  } else {
    const months = Math.floor(days / 30);
    return `${months} month${months !== 1 ? 's' : ''} ago`;
  }
};

// HTML Content Renderer Component
const HTMLContentRenderer = ({ content, className = '' }) => {
  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current && content) {
      // Sanitize and render HTML content
      contentRef.current.innerHTML = content;

      // Make all links open in new tab and add proper styling
      const links = contentRef.current.querySelectorAll('a');
      links.forEach(link => {
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.className = 'text-blue-400 hover:text-blue-300 underline transition-colors';
      });
    }
  }, [content]);

  return (
    <div
      ref={contentRef}
      className={`prose prose-invert max-w-none leading-relaxed ${className}`}
      style={{
        // Custom styles for rendered HTML content
        '--tw-prose-body': '#e5e7eb',
        '--tw-prose-headings': '#ffffff',
        '--tw-prose-links': '#60a5fa',
        '--tw-prose-bold': '#ffffff',
        '--tw-prose-counters': '#9ca3af',
        '--tw-prose-bullets': '#6b7280',
        '--tw-prose-hr': '#374151',
        '--tw-prose-quotes': '#e5e7eb',
        '--tw-prose-quote-borders': '#374151',
        '--tw-prose-captions': '#9ca3af',
        '--tw-prose-code': '#e5e7eb',
        '--tw-prose-pre-code': '#e5e7eb',
        '--tw-prose-pre-bg': '#1f2937',
        '--tw-prose-th-borders': '#374151',
        '--tw-prose-td-borders': '#374151',
      }}
    />
  );
};

const BlogDetail = () => {
  const { id } = useParams();
  const { user, getAuthenticatedApi } = useAuth();
  const { checkForNewBadges } = useBadge();
  const [blog, setBlog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const commentInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBlogAndComments = async () => {
      setIsLoading(true);
      try {
        const authenticatedApi = getAuthenticatedApi();

        // Fetch blog data using axios
        const blogRes = await authenticatedApi.get(`/blog/${id}`);
        const blogData = blogRes.data;

        // Fetch comments using axios
        const commentsRes = await authenticatedApi.get(`/comments/blog/${blogData.id}`);
        const commentsData = commentsRes.data || { comments: [] };

        const formattedComments = commentsData.comments.map((c) => ({
          id: c.id,
          author: c.user?.name || 'Anonymous',
          authorId: c.user?.id,
          content: c.content,
          created_at: c.created_at,
          likes: 0,
          post_id: c.post_id,
          parent_id: c.parent_id,
        }));

        // Determine if the current user has liked the blog
        const userHasLiked = blogData.user_reaction === 'LIKE';

        setBlog({
          ...blogData,
          imageUrl: blogData.cover_image ? `https://app.writeapp.in${blogData.cover_image}` : null,
          author: blogData.author?.name || blogData.author?.email || 'Anonymous',
          authorId: blogData.author?.id,
          comments: formattedComments.length,
          commentsList: formattedComments,
          like_count: blogData.like_count || 0,
          userHasLiked,
          tags: blogData.tags?.map((tag) => tag.name) || [],
        });

      } catch (error) {
        console.error('Error fetching blog or comments:', error);
        navigate('/blog', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogAndComments();
  }, [id, navigate, user, getAuthenticatedApi]);

  const handleLike = async () => {
    if (!user) {
      navigate('/login', { state: { from: `/blog/${id}` } });
      return;
    }
    // Prevent liking if already liked
    if (blog.userHasLiked) return;

    try {
      const authenticatedApi = getAuthenticatedApi();
      const response = await authenticatedApi.post(`/blog/${blog.id}/reactions`, { type: 'LIKE' });

      if (response.status === 200 || response.status === 201) {
        setBlog((prev) => ({
          ...prev,
          like_count: prev.like_count + 1,
          userHasLiked: true,
        }));
        checkForNewBadges?.();
      }
    } catch (error) {
      console.error('Error updating blog reaction:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login', { state: { from: `/blog/${id}` } });
      return;
    }

    if (!comment.trim()) return;
    setIsSubmitting(true);

    try {
      const authenticatedApi = getAuthenticatedApi();
      const response = await authenticatedApi.post(
        `/comments/blog/${blog.id}`,
        { content: comment.trim(), blog_id: blog.id }
      );

      const newComment = {
        id: response.data.id,
        author: user.name || 'You',
        authorId: user.id || user.user_id,
        content: comment.trim(),
        created_at: new Date().toISOString(),
        likes: 0,
        post_id: blog.id,
        parent_id: null,
      };

      setBlog((prev) => ({
        ...prev,
        comments: prev.comments + 1,
        commentsList: [newComment, ...prev.commentsList],
      }));

      setComment('');
      checkForNewBadges?.();
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToComments = () => {
    commentInputRef.current?.scrollIntoView({ behavior: 'smooth' });
    commentInputRef.current?.focus();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0B0C0D] text-white">
        <Navbar currentPage="blog" />
        <div className="flex justify-center items-center h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-[#0B0C0D] text-white">
        <Navbar currentPage="blog" />
        <div className="flex flex-col justify-center items-center h-[calc(100vh-4rem)]">
          <h2 className="text-2xl font-bold mb-4">Blog not found</h2>
          <Link to="/blog" className="text-primary hover:underline">Back to Blogs</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C0D] text-white">
      <Navbar currentPage="blog" />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back button */}
        <Link
          to="/blog"
          className="group text-white py-1.5 rounded-md flex items-center w-fit transition-all duration-200 backdrop-blur-sm mb-4 bg-transparent"
        >
          <svg
            className="w-4 h-4 mr-2 transform transition-transform duration-200 group-hover:-translate-x-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          <span className="text-sm">Back to Blogs</span>
        </Link>

        {/* Blog header */}
        <h1 className="text-3xl md:text-4xl font-bold mb-4">{blog.title}</h1>

        <div className="flex items-center text-gray-400 text-sm mb-6">
          <span className="mr-2">By</span>
          <span className="text-gray-400 mr-2">
            {blog.author}
          </span>

          •<span className="ml-2">{formatDistanceToNow(blog.created_at)}</span>
        </div>

        {/* Featured image */}
        {blog.imageUrl && (
          <img
            src={blog.imageUrl}
            alt={blog.title}
            className="rounded-lg mb-8 w-full object-cover max-h-[400px]"
          />
        )}

        {/* Blog content with HTML rendering */}
        <div className="mb-8">
          <HTMLContentRenderer
            content={blog.content}
            className="blog-content"
          />
        </div>

        {/* Tags */}
        {blog.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {blog.tags.map((tag, index) => (
              <span
                key={index}
                className="bg-gray-700 text-sm px-3 py-1 rounded-full text-gray-300"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Like and comment buttons */}
        <div className="flex items-center space-x-6 mb-8">
          <button
            onClick={handleLike}
            disabled={blog.userHasLiked}
            className={`flex items-center space-x-2 transition-colors ${blog.userHasLiked
              ? 'text-blue-400 cursor-not-allowed'
              : 'text-gray-400 hover:text-blue-400'
              }`}
          >
            <svg
              className={`w-5 h-5 ${blog.userHasLiked ? 'fill-current' : ''}`}
              fill={blog.userHasLiked ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            <span>{blog.like_count} {blog.like_count === 1 ? 'Like' : 'Likes'}</span>
          </button>

          <button
            onClick={scrollToComments}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <span>{blog.comments} {blog.comments === 1 ? 'Comment' : 'Comments'}</span>
          </button>
        </div>

        {/* Comment section */}
        <div ref={commentInputRef}>
          <form onSubmit={handleCommentSubmit} className="mb-6">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={user ? 'Add a comment...' : 'Please login to comment'}
              className="w-full bg-gray-800 text-white rounded-lg p-4 resize-none mb-4 focus:outline-none focus:ring-2 focus:ring-primary"
              rows="3"
              disabled={!user}
            />
            <button
              type="submit"
              disabled={isSubmitting || !user || !comment.trim()}
              className="bg-primary hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md transition-colors"
            >
              {isSubmitting ? 'Posting...' : 'Post Comment'}
            </button>
          </form>

          {/* Comments list */}
          <div className="space-y-4">
            {blog.commentsList.length === 0 ? (
              <p className="text-gray-400 text-center py-8">
                No comments yet. Be the first to comment!
              </p>
            ) : (
              blog.commentsList.map((comment) => (
                <motion.div
                  key={comment.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gray-800 p-4 rounded-lg"
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center text-sm text-white">
                        {comment.author[0].toUpperCase()}
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-400">
                          {comment.author}
                        </span>

                        <p className="text-xs text-gray-400">
                          {formatDistanceToNow(comment.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-300 whitespace-pre-wrap">{comment.content}</p>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Custom styles for blog content */}
      <style jsx global>{`
        .blog-content h1 {
          font-size: 1.875rem;
          font-weight: bold;
          margin: 1.5rem 0 1rem 0;
          line-height: 1.2;
          color: #ffffff;
        }
        
        .blog-content h2 {
          font-size: 1.5rem;
          font-weight: bold;
          margin: 1.25rem 0 0.75rem 0;
          line-height: 1.3;
          color: #ffffff;
        }
        
        .blog-content h3 {
          font-size: 1.25rem;
          font-weight: bold;
          margin: 1rem 0 0.5rem 0;
          line-height: 1.4;
          color: #ffffff;
        }
        
        .blog-content p {
          margin: 0.75rem 0;
          line-height: 1.7;
          color: #e5e7eb;
        }
        
        .blog-content ul, 
        .blog-content ol {
          margin: 1rem 0;
          padding-left: 2rem;
          color: #e5e7eb;
        }
        
        .blog-content li {
          margin: 0.5rem 0;
          line-height: 1.6;
        }
        
        .blog-content a {
          color: #60a5fa;
          text-decoration: underline;
          transition: color 0.2s;
        }
        
        .blog-content a:hover {
          color: #93c5fd;
        }
        
        .blog-content hr {
          border: none;
          border-top: 1px solid #4b5563;
          margin: 2rem 0;
        }
        
        .blog-content strong {
          font-weight: bold;
          color: #ffffff;
        }
        
        .blog-content em {
          font-style: italic;
        }
        
        .blog-content u {
          text-decoration: underline;
        }
        
        .blog-content s {
          text-decoration: line-through;
        }
        
        .blog-content blockquote {
          border-left: 4px solid #4b5563;
          padding-left: 1rem;
          margin: 1rem 0;
          font-style: italic;
          color: #d1d5db;
        }
        
        .blog-content code {
          background-color: #374151;
          padding: 0.125rem 0.25rem;
          border-radius: 0.25rem;
          font-family: 'Courier New', monospace;
          font-size: 0.875rem;
        }
        
        .blog-content pre {
          background-color: #1f2937;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1rem 0;
        }
        
        .blog-content pre code {
          background: none;
          padding: 0;
        }
        
        .blog-content img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1rem 0;
        }
        
        .blog-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
        }
        
        .blog-content th,
        .blog-content td {
          border: 1px solid #374151;
          padding: 0.5rem;
          text-align: left;
        }
        
        .blog-content th {
          background-color: #374151;
          font-weight: bold;
        }
      `}</style>
    </div>
  );
};

export default BlogDetail;